import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ReceiptText } from 'lucide-react';
import { getPaymentOptions } from '../../redux/paymentSlice';

// Phase-1 is offline-only: cash is collected in person; card/online are recorded.
// When the backend payment config hasn't returned any methods yet (e.g. the
// payment branch isn't deployed on this environment), fall back to these so the
// booking step always asks instead of going blank. supportedTiming mirrors the
// backend PaymentSetting defaults. There is no "pay later" — every appointment
// must be settled before it can be completed.
const DEFAULT_METHODS = [
  { key: 'cash', label: 'Cash', requiresGateway: false, supportedTiming: 'postpaid' },
  { key: 'card', label: 'Card / Terminal', requiresGateway: false, supportedTiming: 'both' },
  { key: 'online', label: 'Online', requiresGateway: false, supportedTiming: 'both' },
];

const TYPE_OPTIONS = [
  { key: 'postpaid', label: 'After the visit' },
  { key: 'prepaid', label: 'Before the visit' },
];

// In-modal payment STEP rendered as a mini-INVOICE: the appointment details sit on
// the left, the payment method/timing/status on the right. Enforces the rule that a
// prepaid (online) booking can never be cash. Emits { method, timing } via onChange.
// `summary` is an array of { label, value } describing the appointment.
export default function PaymentMethodSelect({ doctorId, value, onChange, summary = [] }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const paymentOptions = useSelector((state) => state.payment?.paymentOptions);

  const [type, setType] = useState(value?.timing || 'postpaid');

  useEffect(() => {
    if (doctorId) dispatch(getPaymentOptions(doctorId));
  }, [doctorId, dispatch]);

  // Offline methods available (from API, else fallback) regardless of timing.
  // Drop the retired 'paylater' method if a stale backend config still returns it.
  const fromApi = (paymentOptions?.methods || []).filter((m) => !m.requiresGateway && m.key !== 'paylater');
  const allMethods = fromApi.length ? fromApi : DEFAULT_METHODS;

  // 'postpaid' | 'prepaid' | 'both' — when 'both' the patient/doctor chooses.
  const policy = paymentOptions?.timing || 'postpaid';
  const method = value?.method || '';
  const activeTiming = policy === 'both' ? type : policy;

  // Methods valid for a given timing: prepaid (online) excludes at-clinic-only
  // methods (cash, pay-later), postpaid accepts everything offline.
  const methodsFor = (timing) =>
    allMethods.filter((m) => {
      const st = m.supportedTiming || 'postpaid';
      return st === 'both' || st === timing;
    });

  const methods = methodsFor(activeTiming);

  // Keep a valid method selected: auto-select the first valid one (so every
  // booking carries a payment record), or clear it when none fit the timing.
  useEffect(() => {
    const keys = methods.map((m) => m.key);
    if (methods.length && (!method || !keys.includes(method))) {
      onChange?.({ method: methods[0].key, timing: activeTiming });
    } else if (!methods.length && method) {
      onChange?.({ method: '', timing: activeTiming });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTiming, method, paymentOptions]);

  const pickMethod = (key) => onChange?.({ method: key, timing: activeTiming });

  const pickType = (key) => {
    setType(key);
    // Re-resolve the method under the new timing (policy is 'both' here so the
    // new active timing is exactly `key`). Drop cash if switching to prepaid.
    const next = methodsFor(key);
    const keep = next.find((m) => m.key === method);
    onChange?.({ method: keep ? method : (next[0]?.key || ''), timing: key });
  };

  const hasSummary = Array.isArray(summary) && summary.length > 0;

  return (
    <div data-testid="payment-method-step" className="rounded-2xl border border-gray-200 overflow-hidden">
      {/* Invoice header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-[#4ABDBD]/5 to-[#3AA8A8]/5">
        <ReceiptText className="w-4 h-4 text-[#4ABDBD]" />
        <h3 className="text-sm font-semibold text-gray-800">
          {t('appointmentmodal.invoice_payment', 'Invoice & payment')}
        </h3>
        <span data-testid="payment-status-pill" className="ml-auto inline-flex items-center text-[11px] font-semibold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
          {t('payments.not_paid', 'Not paid')}
        </span>
      </div>

      {/* Appointment details | Payment — laid out horizontally */}
      <div className={`grid grid-cols-1 ${hasSummary ? 'sm:grid-cols-2' : ''} divide-y sm:divide-y-0 sm:divide-x divide-gray-100`}>
        {hasSummary && (
          <div className="p-4 space-y-2" data-testid="payment-invoice-summary">
            <p className="text-[11px] font-bold uppercase tracking-wide text-gray-400">
              {t('appointmentmodal.appointment_details', 'Appointment details')}
            </p>
            {summary.map((row) => (
              <div key={row.label} className="flex items-start justify-between gap-3 text-sm">
                <span className="text-gray-500">{row.label}</span>
                <span className="font-semibold text-gray-700 text-right break-words">{row.value}</span>
              </div>
            ))}
          </div>
        )}

        <div className="p-4 space-y-3">
          <p className="text-[11px] font-bold uppercase tracking-wide text-gray-400">
            {t('appointmentmodal.payment_method', 'Payment method')}
          </p>

          {policy === 'both' && (
            <div data-testid="payment-type-options">
              <p className="text-xs font-medium text-gray-500 mb-1.5">
                {t('appointmentmodal.payment_type', 'When to pay')}
              </p>
              <div className="grid grid-cols-2 gap-2">
                {TYPE_OPTIONS.map((o) => (
                  <button
                    key={o.key}
                    type="button"
                    onClick={() => pickType(o.key)}
                    data-testid={`payment-type-${o.key}`}
                    className={`text-sm px-3 py-2 rounded-xl border transition ${
                      activeTiming === o.key
                        ? 'border-[#4ABDBD] bg-[#4ABDBD]/10 text-[#3AA8A8] font-semibold'
                        : 'border-gray-200 text-gray-600 hover:border-[#4ABDBD]'
                    }`}
                  >
                    {t(`appointmentmodal.payment_type_${o.key}`, o.label)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {methods.length > 0 ? (
            <div data-testid="payment-method-options" role="radiogroup" aria-label={t('appointmentmodal.payment_method', 'Payment method')}>
              <p className="text-xs font-medium text-gray-500 mb-1.5">
                {t('payments.method', 'Method')}
              </p>
              {/* Methods are a single-select radio list — visually distinct from the
                  pill-style "When to pay" timing buttons above. */}
              <div className="flex flex-wrap gap-2">
                {methods.map((m) => {
                  const selected = method === m.key;
                  return (
                    <label
                      key={m.key}
                      data-testid={`payment-method-${m.key}`}
                      className={`flex items-center w-fit gap-3 px-3.5 py-3 rounded-xl border cursor-pointer transition-all duration-200 ${
                        selected
                          ? 'border-[#4ABDBD] bg-[#4ABDBD]/5 ring-1 ring-[#4ABDBD]/30 shadow-sm'
                          : 'border-gray-200 hover:border-[#4ABDBD]/60 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="payment-method"
                        value={m.key}
                        checked={selected}
                        onChange={() => pickMethod(m.key)}
                        className="sr-only"
                      />
                      {/* Custom radio dial */}
                      <span
                        aria-hidden="true"
                        className={`flex items-center justify-center h-5 w-5 rounded-full border-2 transition-colors duration-200 ${
                          selected ? 'border-[#4ABDBD]' : 'border-gray-300'
                        }`}
                      >
                        <span
                          className={`h-2.5 w-2.5 rounded-full bg-[#4ABDBD] transition-transform duration-200 ${
                            selected ? 'scale-100' : 'scale-0'
                          }`}
                        />
                      </span>
                      <span className={`text-sm ${selected ? 'text-[#3AA8A8] font-semibold' : 'text-gray-700 font-medium'}`}>
                        {m.label}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          ) : (
            <p data-testid="payment-no-methods-for-timing" className="text-xs text-amber-600">
              {t('appointmentmodal.no_method_for_prepaid', 'No online payment method is available yet. Choose “After the visit”.')}
            </p>
          )}

          <p className="text-[11px] text-gray-400">
            {activeTiming === 'prepaid'
              ? t('appointmentmodal.payment_prepaid_hint', 'Paid online in advance; cash is not available for advance payment.')
              : t('appointmentmodal.payment_collected_at_clinic', 'Collected at the clinic; the doctor marks it paid on the Payments page.')}
          </p>
        </div>
      </div>
    </div>
  );
}
